import type { TrackerFetcher } from '@/provider/TrackerProvider.types'
import type { ApiErrorPayload, ApiFetchOptions } from './api.types'

export type { ApiErrorPayload } from './api.types'

function hasApiErrorDetails(
  value: object
): value is { error?: unknown; field?: unknown } {
  return 'error' in value || 'field' in value
}

export function readApiErrorPayload(value: unknown): ApiErrorPayload | null {
  if (
    typeof value !== 'object' ||
    value === null ||
    !hasApiErrorDetails(value)
  ) {
    return null
  }

  const field = typeof value.field === 'string' ? value.field : undefined

  if (!('error' in value)) {
    if (field === undefined) {
      return null
    }

    return { field }
  }

  const message =
    typeof value.error === 'string' ? value.error : String(value.error)

  if (field === undefined) {
    return { message }
  }

  return { message, field }
}

/**
 * The shared error currency across both shells. Each shell's fetcher throws
 * this on a non-2xx response (the free shell parses fetch Responses; the
 * embedded shell maps axios errors). Feature code can `instanceof`-check it.
 */
export class ApiError extends Error {
  status: number
  retryAfter?: number
  field?: string
  constructor(
    status: number,
    message: string,
    retryAfter?: number,
    field?: string
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.retryAfter = retryAfter
    this.field = field
  }
}

/**
 * Thin wrapper over the injected transport. Non-React data code takes the
 * fetcher as a parameter (spec §5.3); the hook layer (queries.ts) supplies it
 * from context. Returns the parsed body typed as T, or null for an empty body.
 */
export async function apiFetch<T = unknown>(
  fetcher: TrackerFetcher,
  path: string,
  opts: ApiFetchOptions = {}
): Promise<T | null> {
  const init: { method?: string; body?: unknown; signal?: AbortSignal } = {}
  if (opts.method !== undefined) init.method = opts.method
  if (opts.body !== undefined) init.body = opts.body
  if (opts.signal !== undefined) init.signal = opts.signal

  const data = await fetcher(path, init)
  return narrow<T>(data)
}

function narrow<T>(data: unknown): T | null {
  if (data === null || data === undefined) return null
  // All API payloads are JSON; a round-trip yields a T-shaped value without a
  // type assertion (Owen's no-`as` rule). Cheap relative to the network call.
  const value: T = JSON.parse(JSON.stringify(data))
  return value
}
